import { redirect } from "react-router-dom";
import localforage from "localforage";
import { enterpriseAPI } from "../..";
import { readHallSettlementMode, type HallSettlementMode } from "../../services/hallSettlementMode";
import { getErrorStatus } from "../../utils/userFacingError";

/**
 * Thrown by a loader when the organization's payment readiness could not be
 * read. The route error page renders it and `code` selects its copy in
 * src/utils/userFacingError.ts: an unknown answer to retry, never a missing
 * permission and never a missing payment method.
 */
export class PaymentReadinessUnavailableError extends Error {
  readonly status = 503;
  readonly code = "payment_readiness_unavailable";

  constructor() {
    super("payment readiness unavailable");
    this.name = "PaymentReadinessUnavailableError";
  }
}

export type OrgPaymentReadiness = "ready" | "not_ready" | "signed_out";

type ReadinessSource =
  | "signed_out"
  | {
      paymentMethodReady: boolean;
      hallSettlementMode?: unknown;
      negotiationBalanceReady?: unknown;
      jwt: string;
    };
let readInFlight: Promise<ReadinessSource> | null = null;

async function readOrgPaymentReadiness(): Promise<ReadinessSource> {
  const jwt = await localforage.getItem("JWT");
  if (typeof jwt !== "string" || jwt === "") return "signed_out";
  let data: unknown;
  try {
    data = (await enterpriseAPI.get("/negotiation-readiness", { headers: { "X-JWT": jwt } })).data;
  } catch (error) {
    // The 401 interceptor already tried to refresh the session: it is gone.
    if (getErrorStatus(error) === 401) return "signed_out";
    throw new PaymentReadinessUnavailableError();
  }
  const ready =
    data !== null && typeof data === "object"
      ? (data as { paymentMethodReady?: unknown }).paymentMethodReady
      : undefined;
  if (typeof ready !== "boolean") throw new PaymentReadinessUnavailableError();
  const body = data as Record<string, unknown>;
  return {
    paymentMethodReady: ready,
    jwt,
    hallSettlementMode: body.hallSettlementMode,
    negotiationBalanceReady: body.negotiationBalanceReady,
  };
}

/**
 * The organization's payment readiness, read now from Enterprise's
 * GET /negotiation-readiness (`paymentMethodReady`).
 *
 * Weekly (the default) keeps radio and TV on paymentMethodReady. Hall uses
 * negotiationBalanceReady when hallSettlementMode is prepaid; an ads parent
 * accepts either rail and its child still enforces the selected product.
 * That field is the one the weekly servers enforce. Payments publishes it into
 * Enterprise's `org_product_activation.payment_method_ready`, and ads-api
 * requires it on every /negotiate route through `EligibleForNegotiation`
 * (CollaboratorNegotiationReady). Enterprise answers it to every signed-in
 * collaborator of the organization, with no RBAC, so no role is exempt and
 * no role is locked out by a payments permission it never needed. Payments'
 * GET /billing/readiness is not used here: it is guarded by `org_payments`
 * view and nothing on the server gates ads or bids on it.
 *
 * Payments decides that projection. In the weekly rail, an approved Pix
 * Automatico authorization with an admitted explicit weekly limit counts as ready;
 * the frontend neither asks a rail-specific endpoint nor infers readiness from
 * the enrollment response itself.
 *
 * Concurrent callers (a parent and a child route loader) share one request.
 * A failed read throws PaymentReadinessUnavailableError, never a stored or
 * guessed answer; a session that is gone answers "signed_out".
 */
export function requireOrgPaymentReadiness(
  mode: "weekly" | "hall" | "any" = "weekly",
): Promise<OrgPaymentReadiness> {
  if (!readInFlight) {
    readInFlight = readOrgPaymentReadiness().finally(() => {
      readInFlight = null;
    });
  }
  return readInFlight.then(async (source) => {
    if (source === "signed_out") return source;
    const weekly = source.paymentMethodReady;
    if (mode === "weekly") return weekly ? "ready" : "not_ready";
    if (mode === "any" && weekly) return "ready";
    const currentMode = await readHallSettlementMode(source.jwt, {
      hallSettlementMode: source.hallSettlementMode as HallSettlementMode | undefined,
      negotiationBalanceReady: source.negotiationBalanceReady as boolean | undefined,
    });
    let hall = weekly;
    if (currentMode === "prepaid") {
      if (typeof source.negotiationBalanceReady !== "boolean")
        throw new PaymentReadinessUnavailableError();
      hall = source.negotiationBalanceReady;
    }
    return (mode === "any" ? weekly || hall : hall) ? "ready" : "not_ready";
  });
}

/**
 * The "payment method configured" gate of the ads route loaders. Returns the
 * redirect to take, or null when the loader may continue.
 *
 * Uses the organization's payment readiness as the servers see it
 * (`requireOrgPaymentReadiness`). "not ready" sends to /403 and a failed read
 * is an error page. An outage must neither open the routes nor claim a missing
 * permission.
 */
export async function paymentMethodGate(
  mode: "weekly" | "hall" | "any" = "weekly",
): Promise<Response | null> {
  const readiness = await requireOrgPaymentReadiness(mode);
  if (readiness === "signed_out") return redirect("/auth");
  return readiness === "ready" ? null : redirect("/403");
}
